import React from "react";

/**
 * Choose the earliest effect available:
 * - useInsertionEffect (React 18+) runs before layout and paint
 * - fallback to useLayoutEffect otherwise
 */
const useEarlyEffect =
  typeof React.useInsertionEffect === "function"
    ? React.useInsertionEffect
    : React.useLayoutEffect;

/**
 * Normalize incoming HTML:
 * - If it already looks like raw HTML ("<"), return as-is (fast path)
 * - Otherwise handle possible JSON-escaped strings
 */
const normalizeHTML = (input) => {
  if (input == null) return "";
  if (typeof input !== "string") return String(input);

  let s = input;

  // Fast path: raw HTML
  if (s.startsWith("<")) return s;

  // Strip one layer of wrapping quotes if present
  if (
    (s.startsWith('"') && s.endsWith('"')) ||
    (s.startsWith("'") && s.endsWith("'"))
  ) {
    s = s.slice(1, -1);
  }

  // If it looks escaped, try to parse as a JSON string then fall back to manual unescape
  if (/[\\][nrt"\\]/.test(s)) {
    try {
      let candidate = s;
      if (!(candidate.startsWith('"') && candidate.endsWith('"'))) {
        candidate = `"${candidate.replace(/"/g, '\\"')}"`;
      }
      return JSON.parse(candidate);
    } catch {
      s = s
        .replace(/\\\\/g, "\\")
        .replace(/\\"/g, '"')
        .replace(/\\n/g, "\n")
        .replace(/\\t/g, "\t")
        .replace(/\\r/g, "\r");
    }
  }

  return s;
};

/**
 * Compute a lightweight id for the content to dedupe submissions.
 */
const computeContentId = (html) => {
  const m = html.match(/name="(merchOrderId|RequestID|InvoiceNo)".*?value="([^"]*)"/is);
  if (m) return `${m[1]}:${m[2]}`;

  const len = Math.min(1024, html.length);
  let h = 0;
  for (let i = 0; i < len; i++) {
    h = (h * 31 + html.charCodeAt(i)) | 0;
  }
  return `h:${len}:${h}`;
};

/**
 * Ensure we submit a given id only once per page lifecycle.
 */
const submitOnce = (id) => {
  const cache = (window.__PGW_SUBMIT_CACHE = window.__PGW_SUBMIT_CACHE || new Set());
  if (cache.has(id)) return false;
  cache.add(id);
  return true;
};

/**
 * Minimal full-screen loading overlay shown while submitting.
 */
const showLoadingOverlay = (message = "Processing payment…") => {
  if (document.getElementById("__pgw_loading_overlay")) return;
  const overlay = document.createElement("div");
  overlay.id = "__pgw_loading_overlay";
  overlay.style.cssText =
    "position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:2147483647;display:flex;align-items:center;justify-content:center;color:#fff;font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;font-size:14px;";
  overlay.innerHTML = `
    <div style="text-align:center;line-height:1.6;">
      <div style="width:40px;height:40px;border:3px solid rgba(255,255,255,0.4);border-top-color:#fff;border-radius:50%;margin:0 auto 12px;animation:__pgw_spin 0.8s linear infinite;"></div>
      <div>${message || "Processing payment…"}</div>
    </div>
  `;
  const style = document.createElement("style");
  style.textContent = "@keyframes __pgw_spin{to{transform:rotate(360deg)}}";
  overlay.appendChild(style);
  document.body.appendChild(overlay);
};

const hideLoadingOverlay = () => {
  const el = document.getElementById("__pgw_loading_overlay");
  if (el && el.parentNode) el.parentNode.removeChild(el);
};

/**
 * Ultra-fast path: parse the first form via regex and submit without DOMParser.
 * - Supports GET (location.replace) and POST (shadow form)
 * - Extracts <input> name/value pairs quickly
 * - Falls back if action is missing
 */
const fastSubmitByRegex = (html) => {
  try {
    const formMatch = html.match(/<form\b([^>]*)>([\s\S]*?)<\/form>/i);
    if (!formMatch) return false;

    const attrs = formMatch[1] || "";
    const body = formMatch[2] || "";

    // Parse attributes into a simple map: key -> value (or "" for boolean attrs)
    const parseAttrs = (str) => {
      const map = {};
      const re = /([^\s=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g;
      let m;
      while ((m = re.exec(str))) {
        const key = (m[1] || "").toLowerCase();
        const val = m[2] ?? m[3] ?? m[4] ?? "";
        if (key) map[key] = val;
      }
      return map;
    };

    const formAttr = parseAttrs(attrs);
    const method = (formAttr.method || "post").toUpperCase();
    const action = formAttr.action;
    if (!action) return false;

    // Collect input fields
    const inputs = [];
    const inputRe = /<input\b([^>]*)>/ig;
    let m;
    while ((m = inputRe.exec(body))) {
      const ia = m[1] || "";
      const iaAttr = parseAttrs(ia);
      const name = iaAttr.name;
      if (!name) continue;
      const type = (iaAttr.type || "text").toLowerCase();
      if (["submit", "button", "image", "file", "reset"].includes(type)) continue;
      if ((type === "checkbox" || type === "radio") && !("checked" in iaAttr)) continue;
      const value = iaAttr.value ?? "";
      inputs.push({ name, value });
    }

    // Fast GET: build query and navigate without rendering
    if (method === "GET") {
      const params = new URLSearchParams();
      inputs.forEach(({ name, value }) => params.append(name, value));
      const sep = action.includes("?") ? "&" : "?";
      const url = action + (params.toString() ? sep + params.toString() : "");
      window.location.replace(url);
      return true;
    }

    // Fast POST: shadow form
    const form = document.createElement("form");
    form.method = "POST";
    form.action = action;
    form.style.cssText =
      "position:fixed;left:-9999px;top:-9999px;width:0;height:0;opacity:0;visibility:hidden;";
    inputs.forEach(({ name, value }) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Fast path: extract the first form and submit immediately with a shadow form.
 */
const fastSubmitForm = (html) => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const form = doc.querySelector("form");
    if (!form) return false;

    const shadowForm = document.createElement("form");

    // Copy attributes (method, action, target, etc.)
    Array.from(form.attributes).forEach((attr) => {
      shadowForm.setAttribute(attr.name, attr.value);
    });

    // Ensure action/method exist
    if (!shadowForm.getAttribute("action") && form.action) {
      shadowForm.setAttribute("action", form.action);
    }
    if (!shadowForm.getAttribute("method") && form.method) {
      shadowForm.setAttribute("method", form.method);
    }

    // Keep it off-screen
    shadowForm.style.cssText =
      "position:fixed;left:-9999px;top:-9999px;width:0;height:0;opacity:0;visibility:hidden;";

    // Copy fields as hidden inputs
    const elements = Array.from(form.elements || []);
    elements.forEach((el) => {
      const tag = (el.tagName || "").toLowerCase();
      const name = el.getAttribute && el.getAttribute("name");
      if (!name) return;

      if (tag === "input") {
        const type = ((el.getAttribute("type") || "text") + "").toLowerCase();
        if (["submit", "button", "image", "file", "reset"].includes(type)) return;
        if (["checkbox", "radio"].includes(type) && !el.checked) return;
        const hidden = document.createElement("input");
        hidden.type = "hidden";
        hidden.name = name;
        hidden.value = el.value != null ? el.value : "";
        shadowForm.appendChild(hidden);
      } else if (tag === "select") {
        const selected = Array.from(el.options || []).filter((o) => o.selected);
        if (selected.length === 0) {
          const hidden = document.createElement("input");
          hidden.type = "hidden";
          hidden.name = name;
          hidden.value = el.value || "";
          shadowForm.appendChild(hidden);
        } else {
          selected.forEach((opt) => {
            const hidden = document.createElement("input");
            hidden.type = "hidden";
            hidden.name = name;
            hidden.value = opt.value;
            shadowForm.appendChild(hidden);
          });
        }
      } else if (tag === "textarea") {
        const hidden = document.createElement("input");
        hidden.type = "hidden";
        hidden.name = name;
        hidden.value = el.value || el.textContent || "";
        shadowForm.appendChild(hidden);
      }
    });

    document.body.appendChild(shadowForm);
    shadowForm.submit();
    return true;
  } catch (e) {
    console.warn("[AyaPay] fastSubmitForm failed; falling back to injection", e);
    return false;
  }
};

/**
 * AyaPay renderer optimized for quickest navigation.
 */
const AyaPay = ({ content, containerId = "main" }) => {
  useEarlyEffect(() => {
    const html = normalizeHTML(content);
    if (!html) return;

    const id = computeContentId(html);
    if (!submitOnce(id)) return;

    // Show loading immediately; keep it if we navigate fast, or hide if we fall back without auto-submit
    showLoadingOverlay();

    // Ultra-fast paths: no DOM paint, submit immediately
    if (fastSubmitByRegex(html) || fastSubmitForm(html)) {
      // Navigation started; keep overlay visible during redirect
      return;
    }

    // Fallback: inject and execute any inline scripts (e.g., onload or explicit submit())
    const mainEl = document.getElementById(containerId);
    if (!mainEl) {
      console.warn(`[AyaPay] Container #${containerId} not found`);
      hideLoadingOverlay();
      return;
    }

    // Heuristic: will scripts auto-submit?
    const willAutoSubmit =
      /\.submit\s*\(/i.test(html) ||
      /\bonload\s*=\s*["'][^"']*submit\s*\(/i.test(html);

    mainEl.innerHTML = html;

    const scripts = Array.from(mainEl.querySelectorAll("script"));
    scripts.forEach((oldScript) => {
      const newScript = document.createElement("script");
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });
      if (oldScript.src) {
        newScript.src = oldScript.src;
      } else {
        newScript.textContent = oldScript.textContent || "";
      }
      oldScript.parentNode?.replaceChild(newScript, oldScript);
    });

    // If no auto-submit intent is detected, remove overlay so UI is usable
    if (!willAutoSubmit) {
      hideLoadingOverlay();
    }
  }, [content, containerId]);

  // Keep DOM minimal; provide a container for fallback injection
  return <div id={containerId} style={{ display: "contents" }} />;
};

export default AyaPay;